import { useState, useEffect, useMemo, useCallback } from 'react';
import { ExternalLink } from 'lucide-react';
import DateRangePicker, { computeRangeForPreset } from '../components/Filters/DateRangePicker';
import useTranslationLanguages from '../hooks/useTranslationLanguages';
import useTranslationOverview from '../hooks/useTranslationOverview';
import useTranslationDetails from '../hooks/useTranslationDetails';
import useTranslationTimeSeries from '../hooks/useTranslationTimeSeries';
import useTranslationLinguistMetrics from '../hooks/useTranslationLinguistMetrics';
import DonutCard from '../components/TranslationDashboard/DonutCard';
import LineChart from '../components/TranslationDashboard/LineChart';
import IssuesBarChart from '../components/TranslationDashboard/IssuesBarChart';
import './TranslationAnomaly.css';
import { toUtcMidnight } from '../utils/dateFormat';

function toDateStr(date) {
  return toUtcMidnight(date).toISOString().split('T')[0];
}

// ─── Language multi-select dropdown ─────────────────────────────────────────

function LanguageDropdown({ availableLanguages, selectedLanguages, onToggle, onSelectAll, loading }) {
  const [open, setOpen] = useState(false);
  const allSelected = selectedLanguages.length === availableLanguages.length;

  const label = loading
    ? 'Loading…'
    : allSelected
    ? 'All'
    : selectedLanguages.length === 0
    ? 'None'
    : selectedLanguages.join(', ');

  return (
    <div className="tl-lang-dropdown" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }} tabIndex={-1}>
      <button className="tl-lang-dropdown__trigger" onClick={() => setOpen((o) => !o)} disabled={loading}>
        <span className="tl-lang-dropdown__value">{label}</span>
        <span className="tl-lang-dropdown__arrow">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="tl-lang-dropdown__menu">
          <label className="tl-lang-dropdown__item tl-lang-dropdown__item--all">
            <input type="checkbox" checked={allSelected} onChange={onSelectAll} />
            All
          </label>
          <div className="tl-lang-dropdown__divider" />
          {availableLanguages.map((lang) => (
            <label key={lang} className="tl-lang-dropdown__item">
              <input
                type="checkbox"
                checked={selectedLanguages.includes(lang)}
                onChange={() => onToggle(lang)}
              />
              {lang}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Filters ────────────────────────────────────────────────────────────────

function Filters({
  availableLanguages,
  languagesLoading,
  selectedLanguages,
  onToggleLanguage,
  onSelectAllLanguages,
  dateRange,
  onDateRange,
}) {
  return (
    <div className="filters-container tl-filters">
      <div className="tl-filters__group">
        <span className="tl-filters__label">Language</span>
        <LanguageDropdown
          availableLanguages={availableLanguages}
          selectedLanguages={selectedLanguages}
          onToggle={onToggleLanguage}
          onSelectAll={onSelectAllLanguages}
          loading={languagesLoading}
        />
      </div>

      <div className="tl-filters__group">
        <span className="tl-filters__label">Time range</span>
        <DateRangePicker
          value={dateRange}
          preset={dateRange.preset}
          onChange={onDateRange}
          enabledPresets={['7d', '14d', '30d', 'custom']}
        />
      </div>
    </div>
  );
}

function SectionError({ error }) {
  if (!error) return null;
  return (
    <div className="tl-section-error">
      {error.message || 'Failed to load data. Please try again.'}
    </div>
  );
}

// ─── Stat cards ─────────────────────────────────────────────────────────────

function thresholdColor(value, thresholds) {
  if (value == null || !thresholds) return null;
  if (value >= thresholds.green) return '#4CAF50';
  if (value >= thresholds.yellow) return '#e6a817';
  return '#F44336';
}

function StatCard({ title, value, unit, loading, thresholds }) {
  const color = thresholdColor(value, thresholds);
  return (
    <div className="tl-card tl-card--stat">
      <h3 className="tl-card__title">{title}</h3>
      {loading ? (
        <div className="tl-card__placeholder">Loading…</div>
      ) : (
        <div className="tl-card__big-number" style={color ? { color } : undefined}>
          {value ?? '—'}
          {unit && <span className="tl-card__unit">{unit}</span>}
        </div>
      )}
    </div>
  );
}

function SuggestionsActionsCount({ data, loading }) {
  const cols = [
    { key: 'applied', label: 'Applied' },
    { key: 'rejected', label: 'Rejected' },
    { key: 'no_action', label: 'No Action' },
    { key: 'total', label: 'TOTAL' },
  ];
  return (
    <div className="tl-card tl-card--stat-grid">
      <h3 className="tl-card__title">Suggestions Actions Count</h3>
      {loading ? (
        <div className="tl-card__placeholder">Loading…</div>
      ) : (
        <div className="tl-stat-grid">
          {cols.map((c) => (
            <div key={c.key} className="tl-stat-grid__cell">
              <div className="tl-stat-grid__label">{c.label}</div>
              <div className="tl-stat-grid__value">{data?.[c.key] ?? '—'}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FoundIssuesCount({ data, loading }) {
  return (
    <div className="tl-card tl-card--stat-grid">
      <h3 className="tl-card__title">Found Issues Count</h3>
      {loading ? (
        <div className="tl-card__placeholder">Loading…</div>
      ) : (
        <div className="tl-stat-grid">
          <div className="tl-stat-grid__cell">
            <div className="tl-stat-grid__label">Issues Found</div>
            <div className="tl-stat-grid__value">{data?.issues_found ?? '—'}</div>
          </div>
          <div className="tl-stat-grid__cell">
            <div className="tl-stat-grid__label">Total Analyzed</div>
            <div className="tl-stat-grid__value">{data?.total_translations_analyzed ?? '—'}</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Details table ───────────────────────────────────────────────────────────

const PAGE_SIZE = 15;

function safeLinkHref(url) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? encodeURI(parsed.href) : null;
  } catch {
    return null;
  }
}

const IS_APPLIED_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
  { value: 'null', label: 'No Action' },
];

function DetailsTable({ data, loading, error }) {
  const [isAppliedFilter, setIsAppliedFilter] = useState('');
  const [userAppliedFilter, setUserAppliedFilter] = useState('');
  const [linkFilter, setLinkFilter] = useState('');
  const [page, setPage] = useState(1);

  // Reset to page 1 whenever filters or source data change
  const resetPage = useCallback(() => setPage(1), []);
  useEffect(() => { resetPage(); }, [isAppliedFilter, userAppliedFilter, linkFilter, data, resetPage]);

  const userOptions = useMemo(() => {
    if (!data) return [];
    const names = [...new Set(data.map((r) => r.user_reacted).filter(Boolean))].sort();
    return names;
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.filter((row) => {
      if (isAppliedFilter === 'true' && row.is_suggestion_applied !== true) return false;
      if (isAppliedFilter === 'false' && row.is_suggestion_applied !== false) return false;
      if (isAppliedFilter === 'null' && row.is_suggestion_applied !== null) return false;
      if (userAppliedFilter && row.user_reacted !== userAppliedFilter) return false;
      if (linkFilter.trim()) {
        try {
          const key = new URL(row.link).search.replace('?', '');
          if (!key.includes(linkFilter.trim())) return false;
        } catch {
          return false;
        }
      }
      return true;
    });
  }, [data, isAppliedFilter, userAppliedFilter, linkFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (loading) return <div className="tl-table-placeholder">Loading details…</div>;
  if (error) return <div className="tl-table-placeholder">Error loading details.</div>;
  if (!data || data.length === 0) return <div className="tl-table-placeholder">No data.</div>;

  return (
    <div className="tl-table-wrapper">
      {/* Column filters */}
      <div className="tl-table-filters">
        <label className="tl-table-filter">
          <span>LINK</span>
          <input
            type="text"
            placeholder="key ID…"
            value={linkFilter}
            onChange={(e) => setLinkFilter(e.target.value)}
            className="tl-table-filter__text"
          />
        </label>
        <label className="tl-table-filter">
          <span>IS APPLIED</span>
          <select value={isAppliedFilter} onChange={(e) => setIsAppliedFilter(e.target.value)}>
            {IS_APPLIED_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="tl-table-filter">
          <span>USER APPLIED</span>
          <select value={userAppliedFilter} onChange={(e) => setUserAppliedFilter(e.target.value)}>
            <option value="">All</option>
            {userOptions.map((u) => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Table body — shows PAGE_SIZE rows */}
        <table className="tl-table">
          <thead>
            <tr>
              <th>LINK</th>
              <th>LANGUAGE</th>
              <th>IS APPLIED</th>
              <th>USER APPLIED</th>
              <th>CURRENT TRANSLATION</th>
              <th>SUGGESTED TRANSLATION</th>
              <th>EXPLANATION</th>
              <th>DETECTED AT</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, i) => (
              <tr key={i}>
                <td>
                  {(() => {
                    const href = safeLinkHref(row.link);
                    const label = (() => { try { return new URL(row.link).search.replace('?', '') || 'Lokalise'; } catch { return 'Lokalise'; } })();
                    return href
                      ? <a href={href} target="_blank" rel="noopener noreferrer"><ExternalLink size={12} /> {label}</a>
                      : <span>{label}</span>;
                  })()}
                </td>
                <td>{row.language_iso}</td>
                <td>
                  {row.is_suggestion_applied === true
                    ? 'Yes'
                    : row.is_suggestion_applied === false
                    ? 'No'
                    : '—'}
                </td>
                <td>{row.user_reacted ?? '—'}</td>
                <td className="tl-table__text-cell"><span className="tl-tooltip" data-tooltip={row.translated_text}>{row.translated_text}</span></td>
                <td className="tl-table__text-cell"><span className="tl-tooltip" data-tooltip={row.suggested_translation}>{row.suggested_translation}</span></td>
                <td className="tl-table__text-cell"><span className="tl-tooltip" data-tooltip={row.explanation}>{row.explanation}</span></td>
                <td>{row.datetime ? String(row.datetime).substring(0, 16) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>

      {/* Footer: count + pagination */}
      <div className="tl-table-footer">
        <span className="tl-table-count">
          Showing {pageRows.length} of {filtered.length}
          {filtered.length !== data.length && ` (filtered from ${data.length})`}
        </span>
        <div className="tl-table-pagination">
          <button
            className="tl-table-page-btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            ‹ Prev
          </button>
          <span className="tl-table-page-info">
            {page} / {totalPages}
          </span>
          <button
            className="tl-table-page-btn"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next ›
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Helper: pivot time-series rows to chart.js format ──────────────────────

function toLineChartData(rows, timeKey, valueKey, labelKey) {
  // rows: [{time, metric/label, value}, ...]  OR  [{time, value1, value2}, ...]
  // For single-metric rows (no labelKey): valueKey is a list of {key, label} pairs
  if (Array.isArray(valueKey)) {
    const sorted = [...rows].sort((a, b) => (a[timeKey] < b[timeKey] ? -1 : 1));
    const labels = sorted.map((r) => r[timeKey].substring(0, 10));
    const datasets = valueKey.map(({ key, label }) => ({
      label,
      data: sorted.map((r) => r[key]),
    }));
    return { labels, datasets };
  }

  // Multi-metric pivot (labelKey groups the rows)
  const metrics = [...new Set(rows.map((r) => r[labelKey]))].sort();
  const timePoints = [...new Set(rows.map((r) => r[timeKey].substring(0, 10)))].sort();
  const datasets = metrics.map((metric) => ({
    label: metric,
    data: timePoints.map((t) => {
      const match = rows.find(
        (r) => r[timeKey].substring(0, 10) === t && r[labelKey] === metric
      );
      return match ? match[valueKey] : null;
    }),
  }));
  return { labels: timePoints, datasets };
}

// ─── Page ────────────────────────────────────────────────────────────────────

function TranslationAnomaly() {
  const [dateRange, setDateRange] = useState(() => ({ ...computeRangeForPreset('30d'), preset: '30d' }));
  const startDate = toDateStr(dateRange.startDate);
  const endDate = toDateStr(dateRange.endDate);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [languagesInitialized, setLanguagesInitialized] = useState(false);

  const { languages: availableLanguages, loading: languagesLoading } = useTranslationLanguages();

  // Select all languages once they load
  useEffect(() => {
    if (!languagesLoading && availableLanguages.length > 0 && !languagesInitialized) {
      setSelectedLanguages(availableLanguages);
      setLanguagesInitialized(true);
    }
  }, [availableLanguages, languagesLoading, languagesInitialized]);

  const { data: overview, loading: overviewLoading, error: overviewError } = useTranslationOverview(
    startDate,
    endDate,
    selectedLanguages
  );
  const { data: details, loading: detailsLoading, error: detailsError } = useTranslationDetails(
    startDate,
    endDate,
    selectedLanguages
  );
  const { data: timeSeries, loading: tsLoading, error: tsError } = useTranslationTimeSeries(
    startDate,
    endDate,
    selectedLanguages
  );
  const { data: linguist, loading: linguistLoading, error: linguistError } = useTranslationLinguistMetrics(
    startDate,
    endDate,
    selectedLanguages
  );

  function handleToggleLanguage(lang) {
    setSelectedLanguages((prev) =>
      prev.includes(lang) ? prev.filter((l) => l !== lang) : [...prev, lang]
    );
  }

  function handleSelectAllLanguages() {
    setSelectedLanguages((prev) =>
      prev.length === availableLanguages.length ? [] : availableLanguages
    );
  }

  // Derived chart data
  const keysIssuesChartData = useMemo(() => {
    if (!timeSeries?.keys_issues_over_time?.length) return null;
    return toLineChartData(timeSeries.keys_issues_over_time, 'time', [
      { key: 'issues_found', label: 'ISSUES FOUND' },
      { key: 'keys_analysed', label: 'KEYS ANALYSED' },
    ]);
  }, [timeSeries]);

  const acceptRejectChartData = useMemo(() => {
    if (!timeSeries?.accept_reject_rate?.length) return null;
    return toLineChartData(timeSeries.accept_reject_rate, 'time', [
      { key: 'apply_success_rate', label: 'APPLY SUCCESS RATE %' },
      { key: 'reject_rate', label: 'REJECT RATE %' },
    ]);
  }, [timeSeries]);

  const acceptRateReactedChartData = useMemo(() => {
    if (!timeSeries?.accept_rate_reacted?.length) return null;
    return toLineChartData(timeSeries.accept_rate_reacted, 'time', [
      { key: 'apply_over_reacted_rate', label: 'APPLY OVER REACTED SUCCESS RATE %' },
    ]);
  }, [timeSeries]);

  const acceptRatePerLanguageChartData = useMemo(() => {
    if (!timeSeries?.accept_rate_per_language?.length) return null;
    return toLineChartData(timeSeries.accept_rate_per_language, 'time', 'value', 'metric');
  }, [timeSeries]);

  const linguistTimeChartData = useMemo(() => {
    if (!linguist?.linguist_time_over_time?.length) return null;
    return toLineChartData(linguist.linguist_time_over_time, 'time', 'value', 'metric');
  }, [linguist]);

  const linguistReactionsChartData = useMemo(() => {
    if (!linguist?.linguist_reactions_over_time?.length) return null;
    return toLineChartData(linguist.linguist_reactions_over_time, 'time', 'value', 'metric');
  }, [linguist]);

  return (
    <div className="application-container">
      <Filters
        availableLanguages={availableLanguages}
        languagesLoading={languagesLoading}
        selectedLanguages={selectedLanguages}
        onToggleLanguage={handleToggleLanguage}
        onSelectAllLanguages={handleSelectAllLanguages}
        dateRange={dateRange}
        onDateRange={setDateRange}
      />

      {/* ── Section 1: Translation Anomaly Checker Metrics ── */}
      <div className="tl-section">
        <h2 className="tl-section__title">Translation Anomaly Checker Metrics</h2>
        <SectionError error={overviewError} />
        <div className="tl-metrics-grid">
          <StatCard
            title="Acceptance Rate on REACTED Issues"
            value={overview?.acceptance_rate}
            thresholds={{ green: 80, yellow: 60 }}
            loading={overviewLoading}
          />
          <DonutCard
            title="Issues Type"
            data={overview?.issues_type}
            loading={overviewLoading}
          />
          <DonutCard
            title="Issues Severity"
            data={overview?.issues_severity}
            loading={overviewLoading}
            colorMap={{
              medium: '#2196F3',
              high: '#FF9800',
              low: '#4CAF50',
              critical: '#F44336',
            }}
          />
          <DonutCard
            title="Suggestion Confidence Score"
            data={overview?.confidence_score}
            loading={overviewLoading}
          />
          <DonutCard
            title="Found Issues"
            data={overview?.found_issues_ratio?.map((d) => ({
              label: d.metric,
              value: d.percentage,
            }))}
            colors={['#F44336', '#4CAF50']}
            loading={overviewLoading}
          />
          <SuggestionsActionsCount
            data={overview?.suggestions_actions_count}
            loading={overviewLoading}
          />
          <DonutCard
            title="Suggestions Actions"
            data={overview?.suggestions_actions_percentage?.map((d) => ({
              label: d.metric,
              value: d.percentage,
            }))}
            colors={['#4CAF50', '#F44336', '#9E9E9E']}
            loading={overviewLoading}
          />
          <FoundIssuesCount data={overview?.found_issues_count} loading={overviewLoading} />
        </div>
      </div>

      {/* ── Section 2: Details table ── */}
      <div className="tl-section">
        <h2 className="tl-section__title">Details Info</h2>
        <DetailsTable data={details} loading={detailsLoading} error={detailsError} />
      </div>

      {/* ── Section 3: Keys/Issues + Issues over time ── */}
      <div className="tl-section">
        <SectionError error={tsError} />
        <LineChart
          title="Keys/Issues"
          data={keysIssuesChartData}
          loading={tsLoading}
          colors={['#F44336', '#2196F3']}
        />
        <IssuesBarChart
          title="Issues"
          data={timeSeries?.issues_over_time}
          loading={tsLoading}
        />
      </div>

      {/* ── Section 4: Accept/reject rate charts ── */}
      <div className="tl-section">
        <SectionError error={tsError} />
        <LineChart
          title="Accept/Reject Rate over FOUND issues"
          data={acceptRejectChartData}
          loading={tsLoading}
        />
        <LineChart
          title="Accept Rate over Reacted issues"
          data={acceptRateReactedChartData}
          loading={tsLoading}
        />
        <LineChart
          title="Accept Rate over Reacted per language"
          data={acceptRatePerLanguageChartData}
          loading={tsLoading}
        />
      </div>

      {/* ── Section 5: KM Team Time Tracker ── */}
      <div className="tl-section">
        <h2 className="tl-section__title">KM Team Time Tracker</h2>
        <SectionError error={linguistError} />
        <div className="tl-row">
          {/* Linguist time table */}
          <div className="tl-card tl-card--table">
            <h3 className="tl-card__title">Linguists time tracking table</h3>
            {linguistLoading ? (
              <div className="tl-card__placeholder">Loading…</div>
            ) : (
              <table className="tl-table">
                <thead>
                  <tr>
                    <th>LINGUIST</th>
                    <th>TOTAL_MINUTES</th>
                  </tr>
                </thead>
                <tbody>
                  {(linguist?.linguist_time ?? []).map((r, i) => (
                    <tr key={i}>
                      <td>{r.linguist}</td>
                      <td>{r.total_minutes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Linguist time graph */}
          <LineChart
            title="Linguists time tracking graph"
            data={linguistTimeChartData}
            loading={linguistLoading}
          />
        </div>

        <div className="tl-row">
          {/* Linguist reaction count table */}
          <div className="tl-card tl-card--table">
            <h3 className="tl-card__title">Linguists reaction count tracking table</h3>
            {linguistLoading ? (
              <div className="tl-card__placeholder">Loading…</div>
            ) : (
              <table className="tl-table">
                <thead>
                  <tr>
                    <th>LINGUIST</th>
                    <th>REACTED</th>
                  </tr>
                </thead>
                <tbody>
                  {(linguist?.linguist_reaction_count ?? []).map((r, i) => (
                    <tr key={i}>
                      <td>{r.linguist}</td>
                      <td>{r.reacted}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Linguist reactions graph */}
          <LineChart
            title="Linguists reaction count tracking graph"
            data={linguistReactionsChartData}
            loading={linguistLoading}
          />
        </div>
      </div>
    </div>
  );
}

export default TranslationAnomaly;
